/*
 *******************************************************************************
 *
 *  Filename:   @/components/auth/NewPasswordReset.js
 *
 *  Syntax:     NA
 *
 *  Synopsis:   Module for auth sign in new password dialog
 *
 *  Author:     Norman J. Nolasco [ Archetact Software ]
 *
 *  Created:    Friday, August 18, 2023 - 6:07 AM (CST)
 *
 *  Notes:
 *
 *  Revisions:
 *              2023/08/18  NJN Initial Build
 *
 *  Copyright (c) 2023 - Archetact Software - All Rights Reserved.
 *  Unauthorized copying of this file via any medium is strictly prohibited.
 *  Proprietary and Confidential.
 *
 *******************************************************************************
 */
import React from "react";
import * as Yup from "yup";
import styled from "@emotion/styled";
import {
  Alert as MuiAlert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField as MuiTextField,
} from "@mui/material";
import { Formik } from "formik";
import { spacing } from "@mui/system";

const Alert = styled(MuiAlert)(spacing);
const TextField = styled(MuiTextField)(spacing);

function NewPasswordReset(props) {
  const passwordValidation = Yup.string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters long")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/,
      "Password must contain at least one uppercase letter, one lowercase letter, one number and one special character",
    );

  const validationSchema = Yup.object().shape({
    newPassword1: passwordValidation,
    newPassword2: Yup.string()
      .oneOf([Yup.ref("newPassword1"), null], "Passwords must match")
      .required("Confirm Password is required"),
  });

  return (
    <Dialog open={props.open} onClose={() => props.setOpen(false)} aria-labelledby="form-dialog-title">
      <DialogTitle id="form-dialog-title">Enter New Password</DialogTitle>
      <Formik
        name="changePasswordForm"
        initialValues={{
          newPassword1: "",
          newPassword2: "",
          submit: false,
        }}
        validationSchema={validationSchema}
        onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
          console.log("submitting");
          try {
            const err = await props.changePassword(props.email, values.newPassword1);
            if (err) {
              if (err.name === "InvalidPasswordException") {
                setStatus({ success: false });
                setErrors({
                  submit: "Password format does not meet the required criteria.",
                });
                setSubmitting(false);
              } else {
                setStatus({ success: false });
                setErrors({ submit: err.message });
                setSubmitting(false);
              }
            } else {
              props.navigate("/dashboard/default");
            }
          } catch (error) {
            console.log("error on submit of change password", error);
          }
        }}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
          <form noValidate onSubmit={handleSubmit}>
            <DialogContent>
              <DialogContentText my={0}>
                Welcome! This is your first time logging in. For security reasons, you must change your password before
                proceeding.
              </DialogContentText>
              <div>
                <ul>
                  <li>be at least 8 characters long</li>
                  <li>contain a combination of upper and lowercase letters, numbers, and symbols</li>
                </ul>
              </div>

              {errors.submit && (
                <Alert mt={2} mb={3} severity="warning">
                  {errors.submit}
                </Alert>
              )}

              <TextField
                autoFocus
                id="newPassword1"
                name="newPassword1"
                label="New Password"
                type="password"
                fullWidth
                value={values.newPassword1}
                error={Boolean(touched.newPassword1 && errors.newPassword1)}
                helperText={touched.newPassword1 && errors.newPassword1}
                onBlur={handleBlur}
                onChange={handleChange}
                mt={2}
                mb={4}
              />

              <TextField
                id="newPassword2"
                name="newPassword2"
                label="Confirm Password"
                type="password"
                fullWidth
                value={values.newPassword2}
                error={Boolean(touched.newPassword2 && errors.newPassword2)}
                helperText={touched.newPassword2 && errors.newPassword2}
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => props.setOpen(false)} color="primary">
                Cancel
              </Button>
              <Button onClick={() => handleSubmit("changePasswordForm")} color="primary" disabled={isSubmitting}>
                Submit
              </Button>
            </DialogActions>
          </form>
        )}
      </Formik>
    </Dialog>
  );
}

export default NewPasswordReset;
